import React, { useState } from 'react';
import api from '../services/api';
import s from './Financeiro.module.css';

const fmtBRL = v => parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDT = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const origem = (l) => {
  if (l.devolucao_id) return `Devolução #${l.devolucao_id}`;
  if (l.venda_id) return `Venda #${l.venda_id}`;
  return 'Avulso';
};

// O valor em R$ (venda que gerou o crédito, ou valor resgatado no débito) é
// só informativo — some na descrição, nunca numa coluna própria, pra não
// parecer que aquele R$ É o crédito (o crédito de verdade é em PONTOS).
const descricaoCompleta = (l) => {
  if (l.valor_ref == null) return l.descricao;
  const rotulo = l.tipo === 'credito' ? 'venda de' : 'resgatou';
  return `${l.descricao} (${rotulo} R$ ${fmtBRL(l.valor_ref)})`;
};

export default function ExtratoFidelidade() {
  const [busca, setBusca]           = useState('');
  const [sugestoes, setSugestoes]   = useState(null); // null = sem busca
  const [clienteSel, setClienteSel] = useState(null);
  const [saldo, setSaldo]           = useState(null);
  const [extrato, setExtrato]       = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');

  const buscarClientes = async () => {
    setErro(''); setSugestoes(null);
    if (!busca.trim()) return;
    try {
      const { data } = await api.get('/clientes', { params: { busca: busca.trim(), limite: 20 } });
      const lista = Array.isArray(data) ? data : (data.rows || []);
      if (lista.length === 1) return selecionar(lista[0]);
      setSugestoes(lista);
    } catch { setErro('Erro ao buscar clientes.'); }
  };

  const selecionar = async (c) => {
    setClienteSel(c);
    setSugestoes(null);
    setBusca(c.nome);
    setErro(''); setCarregando(true);
    try {
      const [{ data: saldoData }, { data: extratoData }] = await Promise.all([
        api.get(`/clientes/${c.id}/fidelidade`),
        api.get(`/clientes/${c.id}/fidelidade/extrato`),
      ]);
      setSaldo(saldoData);
      setExtrato(extratoData);
    } catch {
      setErro('Erro ao carregar extrato de pontos.');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>⭐</span> Extrato de Pontos de Fidelidade</div>

      <div className={s.toolbar} style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 8 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, flex: 1, minWidth: 240, maxWidth: 380 }}>
          Cliente (nome, CPF/CNPJ ou telefone)
          <input className={s.inputErp} value={busca} autoFocus
            onChange={e => { setBusca(e.target.value); setClienteSel(null); }}
            onKeyDown={e => e.key === 'Enter' && buscarClientes()}
            placeholder="Digite e pressione Enter..." />
        </label>
        <button className={`${s.btnErp} ${s.btnErpPrimario}`} onClick={buscarClientes}>Buscar</button>
      </div>

      {erro && <div className={s.erroMsg}>{erro}</div>}

      {sugestoes !== null && !clienteSel && (
        <div className={s.conteudo} style={{ paddingTop: 8 }}>
          {sugestoes.length === 0
            ? <div style={{ color: '#888', fontSize: 12 }}>Nenhum cliente encontrado.</div>
            : (
              <table className={s.tabela}>
                <thead><tr><th style={{ width: 70 }}>Cód.</th><th>Cliente</th><th style={{ width: 140 }}>CPF/CNPJ</th><th style={{ width: 90 }}></th></tr></thead>
                <tbody>
                  {sugestoes.map(c => (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => selecionar(c)}>
                      <td>{c.codigo}</td>
                      <td>{c.nome}</td>
                      <td>{c.cpf_cnpj || '—'}</td>
                      <td>
                        <button className={s.btnErp} style={{ height: 20, fontSize: 11, padding: '0 8px' }}
                          onClick={e => { e.stopPropagation(); selecionar(c); }}>Selecionar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </div>
      )}

      {clienteSel && saldo && (
        <div className={s.conteudo}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 10px', marginBottom: 10,
            background: '#eef2fa', border: '1px solid #c5d5e8', borderRadius: 4, fontSize: 12 }}>
            <span><strong>{clienteSel.nome}</strong></span>
            <span>Saldo atual: <strong>{saldo.saldo_pontos} ponto(s)</strong></span>
            <span style={{ color: '#15803d' }}>Disponível pra resgate: <strong>R$ {fmtBRL(saldo.credito_disponivel)}</strong></span>
            {saldo.pontos_faltantes_proximo_resgate > 0 && (
              <span>Faltam <strong>{saldo.pontos_faltantes_proximo_resgate}</strong> ponto(s) pro próximo lote</span>
            )}
            {!saldo.ativo && <span style={{ color: '#b91c1c' }}>⚠ Programa de fidelidade está inativo</span>}
          </div>

          {carregando
            ? <div style={{ color: '#888', fontSize: 12 }}>Carregando...</div>
            : !extrato || extrato.length === 0
              ? <div style={{ color: '#888', fontSize: 12 }}>Nenhum lançamento de pontos ainda.</div>
              : (
                <table className={s.tabela}>
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>Data/Hora</th>
                      <th style={{ width: 150 }}>Origem</th>
                      <th>Descrição</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Créditos (pts)</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Débitos (pts)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {extrato.map(l => (
                      <tr key={l.id}>
                        <td style={{ fontSize: 11 }}>{fmtDT(l.criado_em)}</td>
                        <td>{origem(l)}</td>
                        <td style={{ fontSize: 11, color: '#555' }}>{descricaoCompleta(l)}</td>
                        <td style={{ textAlign: 'right', color: '#15803d', fontWeight: l.tipo === 'credito' ? 700 : 400 }}>
                          {l.tipo === 'credito' ? l.pontos : ''}
                        </td>
                        <td style={{ textAlign: 'right', color: '#b91c1c', fontWeight: l.tipo === 'debito' ? 700 : 400 }}>
                          {l.tipo === 'debito' ? l.pontos : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
        </div>
      )}

      {!clienteSel && sugestoes === null && (
        <div style={{ color: '#888', fontSize: 12, padding: 24, textAlign: 'center' }}>
          Busque um cliente para ver o extrato de pontos (créditos ganhos nas compras e débitos de resgates/estornos).
        </div>
      )}
    </div>
  );
}
